import { useGlobalState, useResponsive } from '@/hooks';
import { ResponsiveCollection } from '@/interfaces';
import { TRACKING_STAGE_LIST } from '@/modules/pmt/constants';
import { useShipmentDetailsContext } from '@/modules/pmt/context';
import { useEmailSyncPopup, useShipmentInfo } from '@/modules/pmt/hooks';
import type { IShipment } from '@/modules/pmt/interfaces';
import { getShipmentSearchStatus } from '@/modules/pmt/utils';
import { gtmClickParcel, gtmViewParcel } from '@/utils';
import { memo } from 'react';
// import { ShipmentActions } from '@/modules/pmt/components/ShipmentActions';
import {
  Container,
  ShipmentCardBottomInfo,
  ShipmentCardHeader,
  ShipmentCardLabel,
  ShipmentCardTopInfo,
  ShipmentCardTopRight,
} from './ShipmentCard.styles';
import { ShipmentCardChip } from './components/ShipmentCardChip';
import { ShipmentCardInfo } from './components/ShipmentCardInfo';
import { ShipmentCardLogo } from './components/ShipmentCardLogo';
import { useTriggerGTMUserSuggestFound, useUpdateTimeoutShipment } from './hooks';

interface IShipmentCardProps {
  shipment: IShipment;
}

const ShipmentCard = ({ shipment }: IShipmentCardProps) => {
  const { isLoggedIn } = useGlobalState();
  const { isDesktopPMT } = useResponsive([ResponsiveCollection.DesktopPMT]);
  const { shipmentLabel } = useShipmentInfo(shipment.data);
  const { setShowDetailsView, currentShipmentDocId, setCurrentShipmentDocId } = useShipmentDetailsContext();
  const { openEmailSyncPopup } = useEmailSyncPopup();

  // TODO: @ngan uncomment this when starting Horus 4 - Phase 2
  // const [isHovered, setIsHovered] = useState(false);

  const shipmentData = shipment.data;
  useUpdateTimeoutShipment(shipmentData, !isLoggedIn ? openEmailSyncPopup : undefined);
  useTriggerGTMUserSuggestFound(shipmentData);

  if (!shipmentData) return null;

  // parse info
  const docId = shipmentData.firestoreDocId;
  const shipmentState = getShipmentSearchStatus(shipmentData);
  const disabled = shipment.isDeleting;
  // Edge case: Shipments of different data source in the same UI will have focus state
  const selected = docId === currentShipmentDocId;
  const hasIssue = shipmentData.hasIssue ?? false;
  const milestone = shipmentData.milestone;
  const stage = shipmentData.stage;

  const onSelectParcel = () => {
    if (disabled) return;
    setCurrentShipmentDocId(shipment.data.firestoreDocId);

    if (!isDesktopPMT) {
      setShowDetailsView(true);
      window.scrollTo(0, 0);
      // Ref: https://www.pivotaltracker.com/n/projects/2537310/stories/183861712
      gtmViewParcel(); // Mobile #1
    }
    const stageIndex = TRACKING_STAGE_LIST.findIndex((stage) => stage.milestones.has(milestone));
    gtmClickParcel({
      stage: stageIndex !== -1 ? stageIndex + 1 : null,
      hasIssue,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelectParcel();
    }
  };

  // TODO: @ngan uncomment this when starting Horus 4 - Phase 2
  // const renderCardBottomRight = () => {
  //   if (isDesktopPMT) return null;
  //   return <ShipmentActions />;
  // };

  return (
    <Container
      disabled={disabled}
      id={docId}
      onClick={onSelectParcel}
      selected={selected}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      // TODO: @ngan uncomment this when starting Horus 4 - Phase 2
      // onMouseEnter={() => setIsHovered(true)}
      // onMouseLeave={() => setIsHovered(false)}
    >
      <ShipmentCardTopInfo>
        <ShipmentCardHeader>
          <ShipmentCardChip hasIssue={hasIssue} milestone={milestone} stage={stage} />
          <ShipmentCardLabel>{shipmentLabel}</ShipmentCardLabel>
        </ShipmentCardHeader>
        <ShipmentCardTopRight>
          <ShipmentCardLogo shipmentState={shipmentState} shipmentData={shipmentData} />
        </ShipmentCardTopRight>
      </ShipmentCardTopInfo>

      <ShipmentCardBottomInfo>
        <ShipmentCardInfo shipmentState={shipmentState} shipmentData={shipmentData} />
        {/* TODO: @ngan uncomment this when starting Horus 4 - Phase 2 */}
        {/* {renderCardBottomRight()} */}
      </ShipmentCardBottomInfo>
    </Container>
  );
};

const MemoizedShipmentCard = memo(ShipmentCard);
export { MemoizedShipmentCard as ShipmentCard };
